class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.onBackgroundClick = this.handleBackgroundClick.bind(this);

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close(),
    );
    this.attachOverlayListeners();
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });

    const stickyCartLink = document.querySelector("#cart-icon-bubble-sticky");

    if (stickyCartLink) {
      stickyCartLink.setAttribute("role", "button");
      stickyCartLink.setAttribute("aria-haspopup", "dialog");
      stickyCartLink.addEventListener("click", (event) => {
        event.preventDefault();
        this.open(stickyCartLink);
      });
      stickyCartLink.addEventListener("keydown", (event) => {
        if (event.code.toUpperCase() === "SPACE") {
          event.preventDefault();
          this.open(stickyCartLink);
        }
      });
    }
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
      document.dispatchEvent(new CustomEvent("ingenio:cart-manual-open"));
    }
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    document.dispatchEvent(new CustomEvent("ingenio:cart-close"));
    if (
      this.activeElement &&
      !this.activeElement.classList.contains("quick-add__submit")
    ) {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id,
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open"),
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState, { open = true } = {}) {
    if (!parsedState?.sections) return;

    const drawerInner = this.querySelector(".drawer__inner");
    if (drawerInner?.classList.contains("is-empty")) {
      drawerInner.classList.remove("is-empty");
    }

    if (typeof parsedState.item_count === "number") {
      this.classList.toggle("is-empty", parsedState.item_count === 0);
      this.dataset.cartItemCount = String(parsedState.item_count);
    } else {
      this.classList.remove("is-empty");
    }

    this.productId = parsedState.id;

    let didUpdate = false;

    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections[section.id];
      if (!sectionHtml) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const sectionInnerHTML = this.getSectionInnerHTML(
        sectionHtml,
        section.selector,
      );

      if (sectionInnerHTML == null) return;

      sectionElement.innerHTML = sectionInnerHTML;
      didUpdate = true;
    });

    if (!didUpdate) return;

    document.dispatchEvent(
      new CustomEvent("ingenio:cart-drawer-rendered", {
        detail: {
          itemCount:
            typeof parsedState.item_count === "number"
              ? parsedState.item_count
              : null,
        },
      }),
    );

    setTimeout(() => {
      this.attachOverlayListeners();
      if (open) this.open();
      else if (this.classList.contains("active")) {
        const container = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        if (container) trapFocus(container, this.querySelector(".drawer__close"));
      }
    });
  }

  handleBackgroundClick(event) {
    const inner = this.querySelector(".drawer__inner");
    if (!inner) return;
    // If click is inside cart-drawer but outside drawer__inner, close
    if (!inner.contains(event.target)) {
      this.close();
    }
  }

  attachOverlayListeners() {
    const overlay = this.querySelector("#CartDrawer-Overlay");
    if (overlay) {
      overlay.addEventListener("click", this.close.bind(this));
    }
    const continueShopping = this.querySelector(".drawer-continue-shopping");
    if (continueShopping) {
      continueShopping.addEventListener("click", this.close.bind(this));
    }

    // Ensure background click-to-close works even if overlay is replaced
    this.removeEventListener("click", this.onBackgroundClick);
    this.addEventListener("click", this.onBackgroundClick);
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    if (!html) return null;

    const parsedHtml = new DOMParser().parseFromString(html, "text/html");
    const target = selector
      ? parsedHtml.querySelector(selector)
      : parsedHtml.body;

    return target ? target.innerHTML : null;
  }
  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
      {
        id: "cart-icon-bubble-sticky",
      },
    ];
  }
  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }
  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  // For cart drawer we want to re-render from the cart-drawer section,
  // not from main-cart-items like the base CartItems.onCartUpdate does.
  connectedCallback() {
    // Override subscription to cartUpdate to use drawer-specific refresh
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (
          event.source === "cart-drawer-items" ||
          event.renderedSections?.includes("cart-drawer") ||
          event.source === "product-form" ||
          event.source === "cart-cross-sell"
        ) {
          return;
        }
        this.onDrawerCartUpdate();
      },
    );
  }

  onDrawerCartUpdate() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const drawerSection = html.querySelector("#CartDrawer");
        const drawerElement = html.querySelector("cart-drawer");
        const currentDrawer = document.querySelector("#CartDrawer");
        if (drawerSection && currentDrawer) {
          currentDrawer.innerHTML = drawerSection.innerHTML;
          const cartDrawer = document.querySelector("cart-drawer");
          const isEmpty = drawerElement?.classList.contains("is-empty") || false;
          cartDrawer?.classList.toggle("is-empty", isEmpty);
          if (drawerElement?.dataset.cartItemCount && cartDrawer) {
            cartDrawer.dataset.cartItemCount = drawerElement.dataset.cartItemCount;
          }
          document.dispatchEvent(
            new CustomEvent("ingenio:cart-drawer-rendered", {
              detail: { itemCount: isEmpty ? 0 : null },
            }),
          );
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-icon-bubble-sticky",
        section: "cart-icon-bubble-sticky",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

